import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Dropdown, Menu, Table } from 'antd'
import { DownOutlined } from '@ant-design/icons'
import { InventoryActAttachmentModal } from '../InventoryOperation'
import {
  acceptInventoryAction,
  acceptInventoryBulkAction,
  rejectInventoryAction,
  rejectInventoryBulkAction,
  generateInventoryAcceptanceAct,
} from '../../../store/inventory'
import { INVENTORY_ACCEPTED, INVENTORY_PENDING, INVENTORY_WORK_FLOW, OPERATIONS } from '../constants'
import { formatDateTime } from '../../../utils/date-format'
import { viewUserAttrs } from '../../../utils/user-utils'

const API_URL = process.env.REACT_APP_API_URL

export const mapEquipment = (equipment) =>
  equipment && equipment.data
    ? equipment.data.attributes.name +
      (equipment.data.attributes.serial_number ? ` (${equipment.data.attributes.serial_number})` : '')
    : '-'

export const mapTargetPlace = ({ target_project, target_stockroom }) =>
  target_project.data ? target_project.data?.attributes.name : target_stockroom.data?.attributes.name

export const mapOperationType = (type) => OPERATIONS[type]?.caption

export const mapCurrentStatus = (status) => INVENTORY_WORK_FLOW[status]?.caption

const InventoriesTable = ({ columnNames, dataSource, pagination, onRefresh, isRowSelection = true }) => {
  const dispatch = useDispatch()

  const [inventory, setInventory] = useState(null)
  const [selectedRows, setSelectedRows] = useState([])
  const [showAttachmentModal, setShowAttachmentModal] = useState(false)

  const onSelectChange = useCallback((_, selectedRows) => {
    setSelectedRows(selectedRows.map(({ id, attributes: { current_status } }) => ({ id, current_status })))
  }, [])

  const rowSelection = {
    selectedRowKeys: selectedRows.map(({ id }) => id),
    onChange: onSelectChange,
  }

  const hasSelected = useMemo(() => selectedRows.length > 0, [selectedRows])

  const generateAcceptanceAct = useCallback(async (inventory) => {
    const response = await dispatch(generateInventoryAcceptanceAct(inventory)).unwrap()
    let url = window.URL.createObjectURL(
      new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      })
    )

    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `acceptance_act_${inventory}.docx`)

    document.body.appendChild(link)

    link.click()
    link.parentNode.removeChild(link)
  }, [])

  const showInventoryActAttachmentModal = useCallback((inventory) => {
    setInventory(inventory)
    setShowAttachmentModal(true)
  }, [])

  const hideInventoryActAttachmentModal = useCallback(() => {
    setInventory(null)
    setShowAttachmentModal(false)
  }, [])

  const onActAttachFinish = useCallback(() => {
    hideInventoryActAttachmentModal()
    onRefresh()
  }, [onRefresh])

  const finishAction = useCallback(() => {
    setSelectedRows([])
    onRefresh()
  }, [onRefresh])

  const acceptAction = useCallback(
    (inventory) => dispatch(acceptInventoryAction(inventory)).unwrap().then(finishAction),
    [finishAction]
  )

  const acceptBulkAction = useCallback(() => {
    let selectedKeys = selectedRows
      .filter(({ current_status }) => current_status === INVENTORY_PENDING)
      .map(({ id }) => id)

    dispatch(acceptInventoryBulkAction(selectedKeys)).unwrap().then(finishAction)
  }, [selectedRows, finishAction])

  const rejectAction = useCallback(
    (inventory) => dispatch(rejectInventoryAction(inventory)).unwrap().then(finishAction),
    [finishAction]
  )

  const rejectBulkAction = useCallback(() => {
    let selectedKeys = selectedRows
      .filter(({ current_status }) => current_status === INVENTORY_PENDING)
      .map(({ id }) => id)

    dispatch(rejectInventoryBulkAction(selectedKeys)).unwrap().then(finishAction)
  }, [selectedRows, finishAction])

  const columns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      align: 'right',
      width: 60,
    },
    {
      title: 'Оборудование',
      dataIndex: ['attributes', 'equipment'],
      key: 'equipment',
      render: mapEquipment,
    },
    {
      title: 'Тип операции',
      dataIndex: ['attributes', 'operation_type'],
      key: 'operation_type',
      render: mapOperationType,
    },
    {
      title: 'Текущий статус',
      dataIndex: ['attributes', 'current_status'],
      key: 'current_status',
      render: mapCurrentStatus,
    },
    {
      title: 'Дата создания',
      dataIndex: ['attributes', 'createdAt'],
      key: 'created_date',
      render: formatDateTime,
    },
    {
      title: 'Дата подтверждения',
      dataIndex: ['attributes', 'accept_date'],
      key: 'accept_date',
      render: formatDateTime,
    },
    {
      title: 'Целевое местоположение',
      dataIndex: 'id',
      key: 'target',
      render: (_, record) => mapTargetPlace(record.attributes),
    },
    {
      title: 'Пользователь создавший',
      dataIndex: ['attributes', 'create_user'],
      key: 'create_user',
      render: viewUserAttrs,
    },
    {
      title: 'Пользователь принявший',
      dataIndex: ['attributes', 'accept_user'],
      key: 'accept_user',
      render: viewUserAttrs,
    },
    {
      title: 'Комментарий',
      dataIndex: ['attributes', 'comment'],
      key: 'comment',
    },
    {
      title: 'АПП',
      dataIndex: ['attributes', 'attachment'],
      key: 'acceptance_act',
      render: (attachment) => {
        if (attachment && attachment.data) {
          const { name, url } = attachment.data.attributes
          const uploadLink = `${API_URL}${url}`
          return (
            attachment && (
              <a href={uploadLink} target="_blank">
                {name}
              </a>
            )
          )
        } else {
          return ''
        }
      },
    },
    {
      title: 'Операции',
      key: 'operations',
      dataIndex: 'id',
      fixed: 'right',
      width: 100,
      render: (id, { attributes: { current_status } }) => {
        const menu = (
          <Menu>
            <Menu.Item
              key="accept"
              type="dashed"
              onClick={() => (hasSelected ? acceptBulkAction() : acceptAction(id))}
              disabled={current_status !== INVENTORY_PENDING}
            >
              Принять
            </Menu.Item>
            <Menu.Item
              key="reject"
              type="dashed"
              onClick={() => (hasSelected ? rejectBulkAction() : rejectAction(id))}
              disabled={current_status !== INVENTORY_PENDING}
            >
              Отменить
            </Menu.Item>
            <Menu.Item
              key="generate_act"
              type="dashed"
              onClick={() => generateAcceptanceAct(id)}
              disabled={current_status !== INVENTORY_ACCEPTED}
            >
              Сформировать АПП
            </Menu.Item>
            <Menu.Item
              key="attach_act"
              type="dashed"
              onClick={() => showInventoryActAttachmentModal(id)}
              disabled={current_status !== INVENTORY_ACCEPTED}
            >
              Приложить АПП
            </Menu.Item>
          </Menu>
        )
        return (
          <Dropdown overlay={menu}>
            <a>
              Действия <DownOutlined />
            </a>
          </Dropdown>
        )
      },
    },
  ]

  const tableColumns = useMemo(
    () => (columnNames ? columns.filter((col) => columnNames.includes(col.key)) : columns),
    [columnNames, selectedRows]
  )

  return (
    <div>
      <Table
        rowKey="id"
        style={{ paddingTop: 20 }}
        columns={tableColumns}
        pagination={pagination}
        dataSource={dataSource}
        rowSelection={isRowSelection && rowSelection}
        bordered
        size="small"
        scroll={{
          x: 2000,
        }}
      />
      <InventoryActAttachmentModal
        inventory={inventory}
        showModal={showAttachmentModal}
        onCancel={hideInventoryActAttachmentModal}
        onFinish={onActAttachFinish}
      />
    </div>
  )
}

export default InventoriesTable
